import type { FeatureRequest, FeatureRequestStatus } from "@/types";

/** Statuses that count as a Product decision: Proceed, Decline, Defer, Clarify. */
export const DECISION_STATUSES: readonly FeatureRequestStatus[] = [
  "approved",
  "rejected",
  "deferred",
  "needs_clarification",
];

export function isDecisionStatus(status: FeatureRequestStatus): boolean {
  return DECISION_STATUSES.includes(status);
}

function toISODate(d: Date): string {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

/**
 * Product response-cycle target (YYYY-MM-DD) for a submission date:
 * submitted Mon–Tue → that Friday; Wed–Sun → the following Wednesday.
 * Mirrors `responseDueDate` in the Go usecase.
 */
export function computeResponseDue(createdAt: string): string {
  const created = new Date(createdAt);
  const day = new Date(created.getFullYear(), created.getMonth(), created.getDate());
  const addByWeekday = [3, 4, 3, 7, 6, 5, 4]; // Sun..Sat → days until the batch response day
  day.setDate(day.getDate() + addByWeekday[day.getDay()]);
  return toISODate(day);
}

/** Response target date for a request, preferring the API-provided value. */
export function responseDue(fr: FeatureRequest): string {
  return fr.response_due?.trim() || computeResponseDue(fr.created_at);
}

/**
 * When Product first decided, if ever. Falls back to the review time for
 * requests that reached a decision status before `decided_at` existed.
 */
export function decidedAt(fr: FeatureRequest): string | null {
  if (fr.decided_at?.trim()) return fr.decided_at;
  if (isDecisionStatus(fr.status) && fr.reviewed_at?.trim()) return fr.reviewed_at;
  const event = (fr.events ?? []).find((e) => isDecisionStatus(e.to_status as FeatureRequestStatus));
  return event?.created_at ?? null;
}

export type ResponseState =
  | { kind: "responded"; due: string; decidedAt: string; late: boolean }
  | { kind: "overdue"; due: string }
  | { kind: "due_today"; due: string }
  | { kind: "awaiting"; due: string };

export function responseState(fr: FeatureRequest, now = new Date()): ResponseState {
  const due = responseDue(fr);
  const decided = decidedAt(fr);
  if (decided) {
    return { kind: "responded", due, decidedAt: decided, late: toISODate(new Date(decided)) > due };
  }
  const today = toISODate(now);
  if (today > due) return { kind: "overdue", due };
  if (today === due) return { kind: "due_today", due };
  return { kind: "awaiting", due };
}

/** Calendar days past the response-due date (0 if not overdue). */
export function daysPastDue(fr: FeatureRequest, now = new Date()): number {
  const due = responseDue(fr);
  const today = toISODate(now);
  const a = new Date(`${due}T00:00:00`).getTime();
  const b = new Date(`${today}T00:00:00`).getTime();
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

/** Awaiting a Product decision past the response-cycle target. */
export function isResponseOverdue(fr: FeatureRequest): boolean {
  return responseState(fr).kind === "overdue";
}

/** Still waiting for Product's first decision (Submitted or Under review, never decided). */
export function isAwaitingDecision(fr: FeatureRequest): boolean {
  return decidedAt(fr) == null && !isDecisionStatus(fr.status);
}
